import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { PlayArrow as PlayArrowIcon, Stop as StopIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import type { Exercise, ExerciseRun } from '@/api/exercises'
import { isGradedRun, isRunDone } from './runPredicates'

type Props = {
  exercise: Exercise
  /** Попытки по этому заданию на уроке, без снятых */
  runs: ExerciseRun[]
  isTeacher: boolean
  /** Запрос по этому заданию в полёте — спиннер рисуем на его кнопке */
  busy: boolean
  /** Любой запрос блока в полёте: нажать второй раз нельзя ни по какой кнопке */
  locked: boolean
  onLaunch: (exercise: Exercise) => void
  onCancel: (exercise: Exercise, active: ExerciseRun[]) => void
  onSolve: (exercise: Exercise, mine: ExerciseRun | null) => void
  onCheck: (runId: number) => void
}

/** Цвет статуса работы: 0% зелёным быть не должен. */
function runStatusColor(run: ExerciseRun): 'success' | 'warning' | 'error' | 'default' {
  if (run.status === 'active') return 'default'
  if (run.status === 'teacher_review') return 'warning'
  if (run.score_percent === null) return 'default'
  if (run.score_percent >= 60) return 'success'
  if (run.score_percent > 0) return 'warning'

  return 'error'
}

/**
 * Плитка одного задания: кнопка действия и состояние попыток класса.
 *
 * Рисуется в двух местах — на позиции элемента внутри темы и в блоке работ,
 * выданных мимо программы. Копия вёрстки разъезжалась бы уже на первой правке.
 */
export function ExerciseTile({
  exercise,
  runs,
  isTeacher,
  busy,
  locked,
  onLaunch,
  onCancel,
  onSolve,
  onCheck,
}: Props) {
  const { t } = useTranslation()

  // Прогресс класса и чипы — только по зачётным работам: тренировка не оценка,
  // и в «сдано из выданных» она сдвигала бы картину.
  const graded = runs.filter(isGradedRun)
  const training = runs.filter((run) => !isGradedRun(run))
  const active = graded.filter((run) => !isRunDone(run))
  const done = graded.filter(isRunDone)

  // Своя работа ученика: у него в списке только его попытки.
  // Выданная учителем важнее самопрактики — её результат идёт в зачёт темы.
  const mineActive = runs.filter((run) => !isRunDone(run))
  const mineActiveGraded = mineActive.filter(isGradedRun)
  const mineDone = runs.filter(isRunDone)
  // Последняя завершённая: по ней ученик и смотрит результат. Не полагаемся на
  // порядок с сервера — сортируем сами: список приходит по убыванию id, и
  // «последний элемент» был бы самой первой попыткой. Ученик, перерешавший
  // задание, видел бы балл первого захода.
  const mineLastDone = latestRun(mineDone)
  const mineOpenTarget = mineActiveGraded[0] ?? mineActive[0] ?? mineLastDone

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2">{exercise.title}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={t(`exercises.types.${exercise.type}.name`)} />
            {exercise.status !== 'ready' && (
              <Chip size="small" color="warning" label={t('exercises.exerciseStatus.draft')} />
            )}
          </Stack>
        </Box>

        {isTeacher ? (
          <Button
            variant="contained"
            size="small"
            // Спиннер вместо иконки: заблокированная кнопка без него читается
            // как сломанная, и учитель бьёт по ней повторно
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            disabled={locked || exercise.status !== 'ready'}
            onClick={() => onLaunch(exercise)}
          >
            {graded.length > 0 ? t('exercises.lesson.relaunch') : t('exercises.lesson.launch')}
          </Button>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant={mineActive.length > 0 ? 'contained' : 'outlined'}
              size="small"
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              disabled={locked || exercise.status !== 'ready'}
              onClick={() => onSolve(exercise, mineOpenTarget)}
            >
              {mineActive.length > 0
                ? t('exercises.lesson.solve')
                : mineLastDone
                  ? t('exercises.lesson.openResult')
                  : t('exercises.lesson.practice')}
            </Button>

            {/* Пройденное задание можно перерешать: кнопка результата ведёт на
                старую попытку, а тренироваться ученик хочет заново. */}
            {mineActive.length === 0 && mineLastDone !== null && (
              <Button
                variant="text"
                size="small"
                disabled={locked || exercise.status !== 'ready'}
                onClick={() => onSolve(exercise, null)}
              >
                {t('exercises.lesson.practice')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {/* Своё состояние ученику: без него пройденное задание выглядело как
          нетронутое — ни балла, ни статуса, только кнопка «Потренироваться». */}
      {!isTeacher && mineLastDone !== null && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            color={runStatusColor(mineLastDone)}
            variant="filled"
            label={
              // На проверке балл не показываем: там лежит автопроверка, а по
              // переводу и открытому ответу она только подсказка учителю. Он
              // поставит своё, и «На проверке · 100%» превратилось бы в 40%.
              mineLastDone.score_percent === null || mineLastDone.status === 'teacher_review'
                ? t(`exercises.status.${mineLastDone.status}`)
                : `${t(`exercises.status.${mineLastDone.status}`)} · ${mineLastDone.score_percent}%`
            }
          />
          {!isGradedRun(mineLastDone) && (
            <Chip size="small" variant="outlined" color="info" label={t('exercises.lesson.trainingRun')} />
          )}
        </Stack>
      )}

      {isTeacher && graded.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(done.length / graded.length) * 100}
            sx={{ mb: 1, borderRadius: 1 }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {graded.map((run) => (
              <Chip
                key={run.id}
                size="small"
                label={`${run.student?.name ?? `#${run.student_id}`}${
                  run.score_percent === null ? '' : ` · ${run.score_percent}%`
                }`}
                // Тот же помощник, что и у ученика: проверенная работа на 0%
                // зелёной быть не должна — именно по цвету учитель и сканирует
                // список класса.
                color={runStatusColor(run)}
                variant={run.status === 'active' ? 'outlined' : 'filled'}
                onClick={() => {
                  // Нерешённую работу проверять нечего: открываем только
                  // сданные. Проверка идёт прямо здесь — уходить с урока ради
                  // одной оценки незачем
                  if (isRunDone(run)) onCheck(run.id)
                }}
              />
            ))}
          </Stack>
          {active.length > 0 && (
            <Button
              size="small"
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
              sx={{ mt: 1 }}
              disabled={locked}
              onClick={() => onCancel(exercise, active)}
            >
              {t('exercises.lesson.cancelActive')}
            </Button>
          )}
        </Box>
      )}

      {/* Тренировки — отдельной строкой под зачётными: по ним видно, что ученик
          перебирал ответы до зачётной работы, но прогресс класса они не двигают */}
      {isTeacher && training.length > 0 && (
        <Box sx={{ mt: graded.length > 0 ? 1.5 : 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('exercises.lesson.trainingRuns')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {trainingChips(training).map((chip) => (
              <Chip
                key={chip.runId}
                size="small"
                variant="outlined"
                color="info"
                label={chip.label}
                onClick={() => onCheck(chip.runId)}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  )
}

/**
 * Чипы тренировок: по одному на ученика, с числом попыток.
 *
 * Показываем последнюю попытку — по ней виден текущий результат, а число
 * попыток говорит, сколько раз ученик перерешивал.
 */
function trainingChips(runs: ExerciseRun[]): { runId: number; label: string }[] {
  const byStudent = new Map<number, ExerciseRun[]>()

  for (const run of runs) {
    const list = byStudent.get(run.student_id) ?? []
    list.push(run)
    byStudent.set(run.student_id, list)
  }

  return [...byStudent.values()].flatMap((list) => {
    const last = latestRun(list)
    if (!last) return []
    const name = last.student?.name ?? `#${last.student_id}`
    const score = last.score_percent === null ? '' : ` · ${last.score_percent}%`
    const attempts = list.length > 1 ? ` (${list.length})` : ''

    return [{ runId: last.id, label: `${name}${score}${attempts}` }]
  })
}

/**
 * Самая поздняя попытка списка.
 *
 * По id, а не по позиции в массиве: сервер отдаёт прогоны по убыванию id, и
 * «последний элемент» — это самая старая попытка.
 */
function latestRun(runs: ExerciseRun[]): ExerciseRun | null {
  return runs.reduce<ExerciseRun | null>(
    (best, run) => (best === null || run.id > best.id ? run : best),
    null,
  )
}
